// Public client-facing proposal page — /proposal/:token (Sales Brain V9, max-2-page style).
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cn = (o, k) => o?.[`${k}_cn`] || o?.[`${k}_en`] || "";
const biList = (arr) => (arr || []).map((x) => x?.cn || x?.en || "");
const RATING_LABELS = [["brand", "品牌 Brand"], ["visual", "视觉 Visual"], ["content", "内容 Content"], ["trust", "信任 Trust"], ["cta", "行动号召 CTA"]];

export default function SalesProposal() {
    const { token } = useParams();
    const [doc, setDoc] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        axios.get(`${API}/sales-brain/shared/${token}`).then((r) => setDoc(r.data)).catch(() => setErr(true));
    }, [token]);

    if (err) return <Shell><p className="text-center py-24 text-cafe-mocha">Proposal not found · 提案不存在</p></Shell>;
    if (!doc) return <Shell><p className="text-center py-24 text-cafe-mocha">Loading…</p></Shell>;

    const p1 = doc.proposal?.page1 || {}, p2 = doc.proposal?.page2 || {};
    const ratings = doc.audit?.ratings || {};
    const best = doc.price?.best_package || {};
    const qe = doc.quote_editable || {};
    const items = (qe.items || []).filter((i) => i.enabled !== false);

    return (
        <Shell>
            <div className="max-w-3xl mx-auto px-6 py-16" data-testid="public-proposal">
                <div className="flex items-center gap-3 mb-4">
                    <span className="font-serif text-xl text-cafe-espresso">kaily<span className="text-cafe-terracotta">.</span> <span className="text-[10px] tracking-[0.12em] text-cafe-mocha font-sans">F&B VISUAL STUDIO</span></span>
                    {doc.doc_settings?.show_partner_logo && doc.doc_settings?.partner_logo_url && (
                        <>
                            <span className="text-cafe-mocha">×</span>
                            <img src={doc.doc_settings.partner_logo_url} alt="partner" className="h-10 w-10 rounded-xl object-cover" data-testid="proposal-partner-logo" />
                        </>)}
                </div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-cafe-olive font-semibold">Growth proposal</p>
                <h1 className="font-serif text-4xl sm:text-5xl text-cafe-espresso mt-3">{doc.business_name}</h1>
                <p className="text-sm text-cafe-mocha mt-2">{cn(doc.snapshot || {}, "industry")}{doc.snapshot?.location ? ` · ${doc.snapshot.location}` : ""}</p>

                <Sec title="Executive Summary · 执行摘要">
                    <p className="text-[15px] border-l-2 border-cafe-olive pl-3">{cn(p1, "exec_summary")}</p>
                </Sec>

                {Object.keys(ratings).length > 0 && (
                    <Sec title="Instagram 快照 · Snapshot">
                        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                            {RATING_LABELS.map(([k, label]) => (
                                <div key={k} className="rounded-xl border border-cafe-espresso/12 bg-white/70 p-3 text-center">
                                    <p className="text-[11px] text-cafe-mocha">{label}</p>
                                    <p className="text-[15px] text-[#B8863B] tracking-wider mt-1">{"★".repeat(ratings[k] || 0)}{"☆".repeat(Math.max(0, 5 - (ratings[k] || 0)))}</p>
                                </div>))}
                        </div>
                    </Sec>)}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10">
                    <div>
                        <h2 className="text-[12px] uppercase tracking-[0.22em] text-red-800 font-semibold mb-3">现状挑战 · Challenges</h2>
                        <ul className="space-y-2 text-[14px] text-cafe-espresso/85">
                            {biList(p1.challenges).map((t, i) => <li key={i} className="flex gap-2"><span className="text-red-700 font-bold">✗</span>{t}</li>)}
                        </ul>
                    </div>
                    <div>
                        <h2 className="text-[12px] uppercase tracking-[0.22em] text-cafe-olive font-semibold mb-3">解决方案 · Solutions</h2>
                        <ul className="space-y-2 text-[14px] text-cafe-espresso/85">
                            {biList(p1.solutions).map((t, i) => <li key={i} className="flex gap-2"><span className="text-cafe-olive font-bold">✓</span>{t}</li>)}
                        </ul>
                    </div>
                </div>

                {best.name && (
                    <Sec title="推荐方案 · Recommended">
                        <div className="rounded-xl p-5 border border-cafe-olive bg-cafe-latte/50">
                            <p className="font-serif text-lg text-cafe-espresso">⭐ {best.name}</p>
                            <p className="text-[13px] text-cafe-mocha mt-1">{cn(best, "reason")}</p>
                        </div>
                    </Sec>)}

                {(doc.content_plan || []).length > 0 && (
                    <Sec title="内容企划 · Content Plan">
                        <div className="grid gap-2.5">
                            {doc.content_plan.map((v) => (
                                <div key={v.no} className="rounded-xl bg-white/70 border border-cafe-espresso/12 p-3.5 text-[13.5px]" data-testid={`content-plan-${v.no}`}>
                                    <p className="font-serif text-cafe-olive">Video {v.no} — {v.title_cn || v.title_en}</p>
                                    <p className="text-cafe-espresso/80 mt-0.5">{v.purpose_cn || v.purpose_en}</p>
                                </div>))}
                        </div>
                    </Sec>)}

                {doc.menu_shoot && (
                    <Sec title="产品 / 菜单拍摄 · Product & Menu Shoot">
                        <div className="rounded-xl border border-cafe-olive bg-white/70 p-4" data-testid="menu-shoot-section">
                            <p className="font-serif text-cafe-espresso mb-2">📷 {doc.menu_shoot.title_cn || doc.menu_shoot.title_en}</p>
                            <ul className="space-y-1.5">
                                {(doc.menu_shoot.points || []).map((pt, i) => <li key={i}>✓ {pt.cn || pt.en}</li>)}
                            </ul>
                        </div>
                    </Sec>)}

                <Sec title="时间线 · Timeline">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        {(p2.timeline || []).map((w, i) => (
                            <div key={i} className="rounded-xl bg-white/70 border border-cafe-espresso/12 p-3.5 text-[13px]">
                                <p className="font-serif text-cafe-olive">Week {w.week}</p>
                                <p className="mt-1 text-cafe-espresso/85">{w.title_cn || w.title_en}</p>
                            </div>))}
                    </div>
                </Sec>

                <Sec title="交付物 · Deliverables">
                    <ul className="space-y-1.5">{biList(p2.deliverables).map((t, i) => <li key={i}>✓ {t}</li>)}</ul>
                </Sec>

                <Sec title="预期成果 · Expected Results">
                    <ul className="space-y-1.5">{biList(p2.expected_results).map((t, i) => <li key={i}>★ {t}</li>)}</ul>
                </Sec>

                {items.length > 0 && (
                    <Sec title="Investment · 投资">
                        <div className="rounded-xl border border-cafe-espresso/12 bg-white overflow-hidden">
                            <table className="w-full text-[13px]">
                                <tbody>
                                    {items.map((it, i) => (
                                        <tr key={i} className="border-t first:border-t-0 border-cafe-espresso/8">
                                            <td className="px-4 py-2.5"><p className="text-cafe-espresso">{it.name}{it.qty > 1 ? ` × ${it.qty}` : ""}</p>
                                                {it.description && <p className="text-cafe-mocha text-[11.5px]">{it.description}</p>}</td>
                                            <td className="text-right px-4 py-2.5">RM {(it.qty * it.unit_price).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                    {Number(qe.discount) > 0 && (
                                        <tr><td className="px-4 py-1.5 text-right text-cafe-mocha">Discount · 折扣</td>
                                            <td className="text-right px-4 py-1.5" style={{ color: "#B0503C" }}>− RM {Number(qe.discount).toLocaleString()}</td></tr>
                                    )}
                                    <tr className="border-t border-cafe-espresso/15 font-medium">
                                        <td className="px-4 py-2.5 text-right">Total · 总计</td>
                                        <td className="text-right px-4 py-2.5" data-testid="proposal-total">RM {Number(qe.total || 0).toLocaleString()}</td>
                                    </tr>
                                    <tr>
                                        <td className="px-4 py-1.5 text-right text-cafe-mocha">Deposit · 订金 {qe.deposit_pct || 50}%</td>
                                        <td className="text-right px-4 py-1.5 text-cafe-olive font-medium">RM {Number(qe.deposit || 0).toLocaleString()}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        {cn(qe, "payment_terms") && <p className="text-[12.5px] text-cafe-mocha mt-3">{cn(qe, "payment_terms")}</p>}
                    </Sec>)}

                <div className="mt-14 pt-8 border-t border-cafe-espresso/10 text-center">
                    <p className="font-serif text-2xl text-cafe-espresso">kaily<span className="text-cafe-terracotta">.</span></p>
                    <p className="text-[12px] text-cafe-mocha mt-2">Kaily F&B Visual Studio · Selangor, Malaysia<br />kailygan.my@gmail.com · +60 18-362 8663</p>
                </div>
            </div>
        </Shell>
    );
}

function Shell({ children }) {
    return <div className="min-h-screen bg-cafe-cream text-cafe-espresso">{children}</div>;
}

function Sec({ title, children }) {
    return (
        <section className="mt-10">
            <h2 className="text-[12px] uppercase tracking-[0.22em] text-cafe-olive font-semibold mb-3">{title}</h2>
            <div className="text-[14px] leading-relaxed text-cafe-espresso/85">{children}</div>
        </section>
    );
}
